import { Slot } from '@radix-ui/react-slot';
import type React from 'react';
import type { ForwardedRef, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

type InputProps = InputHTMLAttributes<HTMLInputElement>;

export type ChangeAction = 'blur' | 'enter' | 'escape';

export type DraftInputProps = InputProps & {
    children?: ReactNode;
    /**
     * Enter inserts a newline instead of committing. Commit happens on blur or
     * Cmd/Ctrl+Enter, so a user who never learns the shortcut still can't lose
     * their text.
     */
    multiline?: boolean;
    /* eslint-disable-next-line @typescript-eslint/no-invalid-void-type 
    -- rule is slightly wrong in this case because assignability of void return is different from undefined return */
    onValueChanged?: (value: InputProps['value'], action: ChangeAction) => boolean | void;
    selectOnFocus?: boolean;
};

/**
 * Adds behavior to an input that tracks focus and ignores external value
 * changes until the value is committed.
 */
export const DraftInput = forwardRef(
    (
        { defaultValue, multiline, onValueChanged, selectOnFocus, value, ...props }: DraftInputProps,
        forwardedRef: ForwardedRef<HTMLInputElement>,
    ) => {
        const Component = props.children ? Slot : 'input';
        const [draftValue, setDraftValue] = useState(value ?? defaultValue);
        const isEditingRef = useRef(false);

        // sync outside changes only when not active
        useEffect(() => {
            if (!isEditingRef.current) {
                setDraftValue(value ?? '');
            }
        }, [value]);

        function commit(action: ChangeAction) {
            if (!isEditingRef.current) {
                return;
            }
            isEditingRef.current = false;

            let committed = onValueChanged?.(draftValue, action);

            // default is to abandon on escape
            committed ??= action !== 'escape';

            // reset draft value if not committed
            if (!committed) {
                setDraftValue(value ?? defaultValue);
            }
        }

        function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
            setDraftValue(event.currentTarget.value);
        }

        function handleFocus(event: React.FocusEvent<HTMLInputElement>) {
            if (event.defaultPrevented) {
                return;
            }
            if (event.type === 'focus') {
                isEditingRef.current = true;
                if (selectOnFocus) {
                    event.currentTarget.select();
                }
            } else if (isEditingRef.current) {
                commit('blur');
            }
        }

        function handleKeyDown(event: React.KeyboardEvent<HTMLElement>) {
            if (event.key === 'Escape') {
                commit('escape');
                event.currentTarget.blur();
                // a multiline field can hold a paragraph; one Escape reverts the field,
                // it shouldn't also close the dialog or exit edit-all mode
                if (multiline) {
                    event.stopPropagation();
                }
            } else if (event.key === 'Enter') {
                // in a multiline field Enter is a newline, the way a native textarea behaves
                if (multiline && !event.metaKey && !event.ctrlKey) {
                    return;
                }
                commit('enter');
                event.currentTarget.blur();
            }
        }

        // use an outer slot to make use of event handler merging
        return (
            <Slot
                onBlur={handleFocus}
                onChange={handleChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
            >
                <Component ref={forwardedRef} {...props} value={draftValue} />
            </Slot>
        );
    },
);

DraftInput.displayName = 'DraftInput';
